import type React from 'react';

import { formatDateTimeWorded } from '@/utils/date';

interface PenMetricTooltipOptions {
    /** Human-readable source label shown to all users (e.g. "Precomputed daily estimate"). */
    sourceLabel: string;
    /**
     * Technical metadata shown only to Optoscale admins, rendered as labelled rows.
     * Keys are the row labels (e.g. "table", "column", "topic") and values are the
     * corresponding identifiers.
     *
     * @example { table: 'daily_estimate', column: 'estimated_weight' }
     * @example { topic: 'bioscope/data/response/1340' }
     */
    debugSource: Record<string, string>;
    /** Timestamp of the data; drives the "Last updated" line. Null suppresses the line. */
    lastUpdated: Date | null;
    /** When true the debugSource rows are rendered. Pass `isOptoScaleAdmin` from usePermissions(). */
    isAdmin: boolean;
    /** Translation function from useTranslation(). */
    t: (key: string) => string;
}

/**
 * Builds the content node for pen overview metric tooltips.
 *
 * Rendered structure (all users):
 *   Source: <sourceLabel>
 *   Last updated: <date>          ← omitted when lastUpdated is null
 *
 * Additional rows for Optoscale admins:
 *   table: daily_estimate         ← one row per debugSource entry
 *   column: estimated_weight
 */
export function buildPenMetricTooltip({
    sourceLabel,
    debugSource,
    lastUpdated,
    isAdmin,
    t,
}: PenMetricTooltipOptions): React.ReactNode {
    const dateStr = lastUpdated ? formatDateTimeWorded(lastUpdated) : null;

    return (
        <span style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <span>
                {t('metricTooltip.source')}: {sourceLabel}
            </span>
            {dateStr && (
                <span>
                    {t('metricTooltip.lastUpdated')}: {dateStr}
                </span>
            )}
            {isAdmin &&
                Object.entries(debugSource).map(([label, value]) => (
                    <span
                        key={label}
                        style={{ opacity: 0.65, fontStyle: 'italic', fontSize: '0.85em' }}>
                        {label}: {value}
                    </span>
                ))}
        </span>
    );
}
